import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type TouchEvent,
} from "react";
import {
  Sparkles,
  LayoutDashboard,
  Bot,
  Search,
  KanbanSquare,
  Scale,
  LineChart,
  FileSearch,
  PencilLine,
  BriefcaseBusiness,
  Mail,
  ShieldCheck,
  Globe,
  Settings as SettingsIcon,
  Menu,
  Activity as ActivityIcon,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import type { Page } from "../../App";
import { EngagementPicker } from "./EngagementPicker";
import { LiveIndicator } from "./LiveIndicator";

const NAV_ITEMS: Array<{ id: Page; label: string; Icon: LucideIcon; code: string }> = [
  { id: "overview", label: "Overview", Icon: LayoutDashboard, code: "01" },
  { id: "agents", label: "Agents", Icon: Bot, code: "02" },
  { id: "prospects", label: "Prospects", Icon: Search, code: "03" },
  { id: "crm", label: "CRM", Icon: KanbanSquare, code: "04" },
  { id: "decisions", label: "CEO Decisions", Icon: Scale, code: "05" },
  { id: "metrics", label: "Metrics", Icon: LineChart, code: "06" },
  { id: "audits", label: "Audits", Icon: FileSearch, code: "07" },
  { id: "posts", label: "Posts", Icon: PencilLine, code: "08" },
  { id: "linkedin", label: "LinkedIn Hub", Icon: BriefcaseBusiness, code: "11" },
  { id: "templates", label: "Templates", Icon: Mail, code: "15" },
  { id: "admin", label: "Admin / Health", Icon: ShieldCheck, code: "12" },
  { id: "activity", label: "Activity", Icon: ActivityIcon, code: "13" },
  { id: "errors", label: "Errors", Icon: AlertTriangle, code: "14" },
  { id: "settings", label: "Settings", Icon: SettingsIcon, code: "09" },
  { id: "landing", label: "Landing", Icon: Globe, code: "10" },
];

interface LayoutProps {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatTime(d: Date) {
  return d.toLocaleTimeString("en-GB", { hour12: false });
}
function formatDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

export function Layout({ children, currentPage, onNavigate }: LayoutProps) {
  const now = useClock();

  // Drawer state (mobile only — sidebar is always visible at lg+).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Body scroll lock + Escape + focus trap while the drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the first nav button on open.
    const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(
      "nav button",
    );
    firstFocusable?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setDrawerOpen(false);
        hamburgerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const root = drawerRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  // Swipe-left to close (mobile gesture).
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchMove = (e: TouchEvent<HTMLElement>) => {
    if (touchStartX.current === null) return;
    const dx = (e.touches[0]?.clientX ?? 0) - touchStartX.current;
    if (dx < -60) {
      closeDrawer();
      touchStartX.current = null;
    }
  };

  const handleNavigate = (page: Page) => {
    onNavigate(page);
    closeDrawer();
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Skip link — only visible when focused via Tab from the URL bar (WCAG 2.4.1). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-3 focus:py-2 focus:rounded-md focus:bg-[var(--color-accent)] focus:text-[var(--color-bg)] focus:font-mono focus:text-xs focus:font-bold"
      >
        Skip to main content
      </a>

      {/* Backdrop — mobile only, behind the drawer. */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed/off-canvas under lg, inline at lg+. */}
      <aside
        ref={drawerRef}
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={drawerOpen ? false : undefined}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        className={`fixed inset-y-0 left-0 z-40 w-72 flex flex-col
          border-r border-[var(--color-border)] bg-[var(--color-bg-elev)]/95 backdrop-blur-sm
          transition-transform duration-200 ease-out
          ${drawerOpen ? "translate-x-0" : "-translate-x-full"}
          lg:relative lg:translate-x-0 lg:z-auto lg:bg-[var(--color-bg-elev)]/70`}
      >
        {/* glowing right edge */}
        <div className="pointer-events-none absolute top-0 right-0 h-full w-px bg-gradient-to-b from-transparent via-[var(--color-accent)]/40 to-transparent" />

        {/* Brand */}
        <div className="px-6 pt-7 pb-6 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <div
              className="size-9 shrink-0 rounded-md bg-[var(--color-accent-soft)] border border-[var(--color-accent)]/30 grid place-items-center"
              aria-hidden="true"
            >
              <Sparkles className="size-4 text-[var(--color-accent)]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-[26px] leading-[0.9] italic">
                <span className="text-accent-gradient">Levier</span>
                <span className="text-[var(--color-text)]"> IA</span>
              </h1>
              <p className="eyebrow mt-1.5">Swarm · Ops Console</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
          <p className="eyebrow px-3 mb-3">Navigation</p>
          {NAV_ITEMS.map((item, i) => {
            const active = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                aria-current={active ? "page" : undefined}
                className={`reveal group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  active
                    ? "text-[var(--color-accent-glow)] bg-[var(--color-accent-soft)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.02]"
                }`}
                style={{ animationDelay: `${80 + i * 40}ms` }}
              >
                <span
                  className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full transition-all duration-300 ${
                    active
                      ? "bg-[var(--color-accent)] shadow-[0_0_8px_var(--color-accent)]"
                      : "bg-transparent"
                  }`}
                />
                <span
                  className={`font-mono text-[10px] w-6 ${
                    active ? "text-[var(--color-accent)]" : "text-[var(--color-text-dim)]"
                  }`}
                >
                  {item.code}
                </span>
                <item.Icon
                  className={`size-4 transition-transform duration-300 ${
                    active ? "scale-110 text-[var(--color-accent)]" : "group-hover:scale-105"
                  }`}
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <span className="font-medium tracking-wide">{item.label}</span>
                {active && (
                  <span className="ml-auto size-1.5 rounded-full bg-[var(--color-accent)] shadow-[0_0_6px_var(--color-accent)]" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Live status block */}
        <div className="px-4 py-4 border-t border-[var(--color-border)] space-y-3">
          <div className="px-3 py-2.5 rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] relative overflow-hidden">
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-[var(--color-accent)]/60 to-transparent" />
            <LiveIndicator />
            <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-[11px] tabular-nums">
              <span className="text-[var(--color-text-dim)]">{formatDate(now)}</span>
              <span className="text-[var(--color-text-muted)] text-right">{formatTime(now)}</span>
            </div>
          </div>
          <p className="px-1 font-mono text-[10px] tracking-wider text-[var(--color-text-dim)]">
            © 2026 · LEVIER IA
          </p>
        </div>
      </aside>

      {/* Main content */}
      <main id="main-content" className="flex-1 overflow-auto relative">
        {/* Top bar */}
        <div className="sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 lg:px-10 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg)]/80 backdrop-blur-sm">
          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="main-navigation"
            className="lg:hidden size-11 -ml-2 grid place-items-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5 transition-colors"
          >
            <Menu className="size-5" strokeWidth={1.5} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onNavigate("overview")}
            aria-label="Go to overview"
            className="lg:hidden flex items-center gap-2 min-w-0 truncate"
          >
            <span className="font-display italic text-lg leading-none">
              <span className="text-accent-gradient">Levier</span>
              <span className="text-[var(--color-text)]"> IA</span>
            </span>
          </button>
          <div className="flex-1" />
          <EngagementPicker />
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
